import { useMutation } from '@tanstack/react-query'
import { Button, Checkbox, Input, Modal, Radio, RadioGroup, Select, TagField } from '@viapeople/components'
import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { PopulationFields } from '../../../../components/PopulationFields'
import { AddCycleRolesModal } from '../../../../components/PopulationFields/AddCycleRolesModal'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { CycleReviewerGroup, CycleReviewerGroupCreateInput, CycleReviewerGroupUpdateInput } from '../../types'

interface CycleReviewersGroupModalProps {
	isOpen: boolean
	onClose: () => void
	cycleId: string
	organizationId: string
	reviewerGroup?: CycleReviewerGroup
}

interface CycleReviwewerGroupFormProps {
	reviewerGroup?: CycleReviewerGroup
	cycleId: string
	organizationId: string
	onSubmit: (data: CycleReviewerGroupCreateInput | CycleReviewerGroupUpdateInput) => void
	onCancel: () => void
	loading: boolean
}

const useCycleReviewerGroupForm = (reviewerGroup?: CycleReviewerGroup) =>
	useForm({
		defaultValues: {
			name: reviewerGroup?.name,
			shownForRoles: reviewerGroup?.shownForRoles.map((role) => ({
				id: role.id,
				label: role.displayName,
			})),
			minReviewers: reviewerGroup?.minReviewers,
			maxReviewers: reviewerGroup?.maxReviewers,
			required: reviewerGroup?.required,
			prefill: reviewerGroup?.prefill,
			optional: reviewerGroup?.optional,
			requireDeclineReason: reviewerGroup?.requireDeclineReason,
			population: {
				roles: reviewerGroup?.population.roles.map((role) => ({
					id: role.id,
					label: role.displayName,
				})),
				whitelistedUsers: reviewerGroup?.population.whitelistedUsers.map((reviewer) => ({
					id: reviewer.id,
					externalId: reviewer.externalId,
					label: `${reviewer.firstName} ${reviewer.lastName}`,
				})),
				blacklistedUsers: reviewerGroup?.population.blacklistedUsers.map((reviewer) => ({
					id: reviewer.id,
					externalId: reviewer.externalId,
					label: `${reviewer.firstName} ${reviewer.lastName}`,
				})),
				blacklistedHierarchyRoles: reviewerGroup?.population.blacklistedHierarchyRoles.map((role) => ({
					id: role.id,
					label: role.displayName,
				})),
				hierarchyLevels: reviewerGroup?.population.hierarchies,
			},
		},
	})

const CycleReviwewerGroupForm: React.FC<CycleReviwewerGroupFormProps> = ({
	reviewerGroup,
	cycleId,
	organizationId,
	onCancel,
	onSubmit: onSubmitProp,
	loading,
}) => {
	const form = useCycleReviewerGroupForm(reviewerGroup)
	const [pickingRoles, setPickingRoles] = useState(false)
	const { handleSubmit, setValue, getValues, watch } = form

	// Watch the optional field to show/hide the requireDeclineReason checkbox
	const isOptional = watch('optional')

	const onSubmit = handleSubmit((data) => {
		const formData = {
			cycleId,
			name: data.name ?? '',
			shownForRoles: data.shownForRoles?.map((role) => role.id) ?? [],
			population: {
				roles: data.population.roles?.map((role) => role.id) ?? [],
				whitelistedUsers: data.population.whitelistedUsers?.map((reviewer) => reviewer.id) ?? [],
				blacklistedUsers: data.population.blacklistedUsers?.map((reviewer) => reviewer.id) ?? [],
				blacklistedHierarchyRoles: data.population.blacklistedHierarchyRoles?.map((role) => role.id) ?? [],
				hierarchyLevels: data.population.hierarchyLevels ?? [],
			},
			minReviewers: Number.parseInt(String(data.minReviewers ?? '0'), 10),
			maxReviewers: Number.parseInt(String(data.maxReviewers ?? '0'), 10),
			required: data.required ?? false,
			prefill: data.prefill ?? false,
			optional: data.optional ?? false,
			requireDeclineReason: data.requireDeclineReason ?? false,
		}

		if (reviewerGroup?.id) {
			onSubmitProp({ ...formData, id: reviewerGroup.id } as CycleReviewerGroupUpdateInput)
		} else {
			onSubmitProp(formData as CycleReviewerGroupCreateInput)
		}
	})

	return (
		<FormProvider {...form}>
			<form onSubmit={onSubmit}>
				<div className='flex flex-col gap-4 p-8'>
					<div className='flex flex-row gap-4'>
						<div className='flex-1'>
							<Controller
								defaultValue={reviewerGroup?.name}
								name='name'
								render={({ field }) => (
									<Input {...field} label='Group Name' placeholder='Enter rater group name' required />
								)}
							/>
						</div>
						<div className='flex-1 flex flex-row gap-2 items-end'>
							<Controller
								name='shownForRoles'
								render={({ field }) => (
									<TagField
										label='Show Group For'
										placeholder='Select Roles'
										{...field}
										onChange={(roles) => {
											setValue('shownForRoles', roles, { shouldDirty: true })
										}}
									/>
								)}
							/>

							<Button
								variant='secondary'
								onClick={(e) => {
									e.preventDefault()
									setPickingRoles(true)
								}}>
								Add cycle roles
							</Button>
						</div>
					</div>
					<div className='flex flex-row gap-4'>
						<div className='flex-1'>
							<Controller
								defaultValue={reviewerGroup?.minReviewers}
								name='minReviewers'
								render={({ field }) => (
									<Input
										{...field}
										label='Min Raters'
										min={0}
										type='number'
										placeholder='Enter # of min raters'
										onBlur={(e) => {
											const target = e.target as HTMLInputElement
											const maxValue = getValues('maxReviewers')
											if (maxValue && target.value && Number.parseInt(target.value, 10) > maxValue) {
												target.setCustomValidity(`Min raters must be less than or equal to ${maxValue}`)
											} else {
												target.setCustomValidity('')
											}
										}}
									/>
								)}
							/>
						</div>
						<div className='flex-1'>
							<Controller
								defaultValue={reviewerGroup?.maxReviewers}
								name='maxReviewers'
								render={({ field }) => (
									<Input
										{...field}
										label='Max Raters'
										type='number'
										placeholder='Enter # of max raters'
										min={0}
										onBlur={(e) => {
											const target = e.target as HTMLInputElement
											const minValue = getValues('minReviewers') ?? 0
											if (target.value && Number.parseInt(target.value, 10) < minValue) {
												target.setCustomValidity(`Max raters must be greater than or equal to ${minValue}`)
											} else {
												target.setCustomValidity('')
											}
										}}
									/>
								)}
							/>
						</div>
						<div className='flex-1'>
							<Controller
								defaultValue={reviewerGroup?.prefill}
								name='prefill'
								render={({ field }) => (
									<Select
										{...field}
										label='Prefill'
										value={field.value ? 'true' : 'false'}
										onChange={(value) => setValue('prefill', value === 'true', { shouldDirty: true })}
										options={[
											{ label: 'Yes', value: 'true' },
											{ label: 'No', value: 'false' },
										]}
									/>
								)}
							/>
						</div>
					</div>

					<div className='flex flex-row gap-4'>
						<Controller
							defaultValue={reviewerGroup?.optional}
							name='optional'
							render={({ field }) => (
								<div>
									<span className='block text-sm font-medium text-gray-700 mb-2'>Reviewer Optionality</span>
									<RadioGroup
										{...field}
										value={field.value ? 'optional' : 'required'}
										onChange={(value) => {
											const isOptional = value === 'optional'
											setValue('optional', isOptional, { shouldDirty: true })
											setValue('required', !isOptional, { shouldDirty: true })
											// Reset requireDeclineReason if switching to required
											if (!isOptional) {
												setValue('requireDeclineReason', false, { shouldDirty: true })
											}
										}}
										size='small'
										variant='primary'>
										<Radio value='required' label='Required' />
										<Radio value='optional' label='Optional (Raters Can Decline)' />
									</RadioGroup>
								</div>
							)}
						/>

						{/* Conditionally show "Require reason" checkbox when Optional is selected */}
						{isOptional && (
							<Controller
								defaultValue={reviewerGroup?.requireDeclineReason}
								name='requireDeclineReason'
								render={({ field }) => (
									<div className='flex items-end'>
										<Checkbox
											{...field}
											checked={field.value ?? false}
											onChange={(e) => setValue('requireDeclineReason', e.target.checked, { shouldDirty: true })}
											label='Require reason for declining'
											size='small'
											variant='primary'
										/>
									</div>
								)}
							/>
						)}
					</div>

					<PopulationFields name='population' cycleId={cycleId} organizationId={organizationId} />
				</div>

				<div className='flex flex-row justify-end gap-4 pb-8 pr-8'>
					<Button
						variant='secondary'
						onClick={(e) => {
							e.preventDefault()
							onCancel()
						}}
						disabled={loading}>
						Cancel
					</Button>
					<Button type='submit' disabled={loading} isLoading={loading}>
						{reviewerGroup ? 'Save Changes' : 'Add Group'}
					</Button>
				</div>
			</form>

			<AddCycleRolesModal
				isOpen={pickingRoles}
				cycleId={cycleId}
				onClose={() => setPickingRoles(false)}
				excludedRoleIds={() => new Set((getValues('shownForRoles') ?? []).map((r: { id: string }) => r.id))}
				onSelect={(cycleRoles) => {
					setValue(
						'shownForRoles',
						cycleRoles.map((r) => ({
							id: r.roleId as string,
							label: r.displayName,
						})),
						{ shouldDirty: true },
					)
					setPickingRoles(false)
				}}
			/>
		</FormProvider>
	)
}

export const CycleReviewersGroupModal: React.FC<CycleReviewersGroupModalProps> = ({
	isOpen,
	onClose,
	reviewerGroup,
	cycleId,
	organizationId,
}) => {
	const mutationSuccess = () => {
		onClose()
	}
	const mutationSettled = () => {
		queryClient.invalidateQueries({
			queryKey: trpc.admin.cycles.reviewerGroups.search.queryKey({ cycleId: cycleId }),
			exact: false,
			refetchType: 'all',
		})
	}
	const toast = useToast()
	const mutationError = (error: unknown) => {
		console.error(error)
		toast.showToast('Error saving rater group', 'error')
	}
	const { mutate: createReviewerGroup, isPending: isCreating } = useMutation(
		trpc.admin.cycles.reviewerGroups.create.mutationOptions({
			onSuccess: mutationSuccess,
			onError: mutationError,
			onSettled: mutationSettled,
		}),
	)
	const { mutate: updateReviewerGroup, isPending: isUpdating } = useMutation(
		trpc.admin.cycles.reviewerGroups.edit.mutationOptions({
			onSuccess: mutationSuccess,
			onError: mutationError,
			onSettled: mutationSettled,
		}),
	)

	const onSubmit = (data: CycleReviewerGroupCreateInput | CycleReviewerGroupUpdateInput) => {
		if (reviewerGroup) {
			updateReviewerGroup(data as CycleReviewerGroupUpdateInput)
		} else {
			createReviewerGroup(data as CycleReviewerGroupCreateInput)
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			fullContent
			fullScreen
			title={reviewerGroup ? 'Edit Rater Group' : 'Create Rater Group'}
			bodyClassName='overflow-y-auto max-h-[90vh]'>
			<CycleReviwewerGroupForm
				reviewerGroup={reviewerGroup}
				cycleId={cycleId}
				organizationId={organizationId}
				onCancel={onClose}
				onSubmit={onSubmit}
				loading={isCreating || isUpdating}
			/>
		</Modal>
	)
}
